import RichText from 'Custom/RichText';
import Image from 'next/image';
import styles from 'styles/pages/whatToExpect.module.scss';
import Link from 'node_modules/next/link';
import ItemBreakpoint from 'Custom/ItemBreakpoint';
import { SE_TABLE_3, SE_LIST_1, SE_LIST_2 } from './side-effects';

import GRAPHIC_1_IN_5 from 'Images/misc/1-in-5-graphic.webp';
import GRAPHIC_MONTH from 'Images/misc/month-graphic.webp';
import GRAPHIC_1_IN_10 from 'Images/misc/1-in-10-graphic.webp';
import CALENDAR_ICON from 'Images/misc/calendar-with-circle.webp';
import { Table } from '../components/Custom/Table';

export const FAQ_WTE_INSERTION_1 = [
  {
    questionTitle: 'what should I do right after insertion?',
    questionContent: (
      <>
        <p>
          Immediately after the implant is placed, you and your healthcare
          professional should check that it is in your arm by feeling for it.
        </p>
        <p>
          If you cannot feel the implant immediately after insertion, the
          implant may not have been inserted, or it may have been inserted
          deeply. A deep insertion may cause problems with locating and removing
          the implant. When the healthcare professional has located the implant,
          it should be removed.
        </p>
      </>
    ),
  },
  {
    questionTitle: 'how should I care for my arm after insertion?',
    questionContent: (
      <p>
        Following the insertion, a trained healthcare professional will cover
        the site with 2 bandages. The top bandage is a pressure bandage to
        minimize bruising and should be left on for 24 hours. The smaller
        bandage should be kept clean, dry, and in place for 3 to 5 days.
      </p>
    ),
  },
  {
    questionTitle: 'will I have bruising?',
    questionContent: (
      <p>
        In a clinical trial, 8.6% of women experienced site reactions. The most
        common complaint was red skin (3.3%), 3% of users reported hematoma, and
        2% of users reported bruising.
      </p>
    ),
  },
  {
    questionTitle: 'can I get an infection?',
    questionContent: (
      <p>
        Infection is possible. If you think you may have an infection, contact
        your healthcare professional.
      </p>
    ),
  },
  {
    questionTitle:
      'should I keep using other forms of contraception after having NEXPLANON inserted?',
    questionContent: (
      <p>
        It depends on when NEXPLANON was inserted relative to your period. Talk
        to your healthcare professional to determine how long you may need to
        use additional contraceptive methods. For example, if NEXPLANON is
        inserted after the fifth day of your period, then you should use
        additional contraceptive methods, such as a condom, for the first 7 days
        after insertion.
      </p>
    ),
  },
  {
    questionTitle: 'what if I want to have NEXPLANON removed?',
    questionContent: (
      <RichText>
        <p>
          A trained healthcare professional can remove the implant at any time
          within the 3-year period. If NEXPLANON is removed, you should use
          another form of contraception, such as condoms, to prevent pregnancy.
        </p>
        <p>
          NEXPLANON prevents pregnancy for up to 3 years. NEXPLANON must be
          removed by the end of the third year.
        </p>
      </RichText>
    ),
  },
];

export const WTE_PERIOD = [
  {
    content: (
      <RichText>
        <div>
          <strong>
            A change to your normal menstrual bleeding pattern is the most
            common side effect of NEXPLANON
          </strong>
        </div>
        <div className={styles.splitBlock}>
          <div>
            <p>
              <strong>1 in 5 women</strong> may stop getting their period
              entirely, while another 1 in 5 women may experience frequent
              and/or prolonged bleeding.
            </p>
            <Image alt="" src={GRAPHIC_1_IN_5} />
            <div className={styles.boxContainer}>
              <Image alt="" src={CALENDAR_ICON} />
              <p>
                After starting NEXPLANON, women are likely to have a change from
                their normal menstrual bleeding pattern. The bleeding pattern
                experienced during the first 3 months of NEXPLANON use is
                broadly predictive of the future bleeding pattern for many
                women.
              </p>
            </div>
          </div>
          <div>
            <p>
              <strong>~1 in 10 women</strong> have NEXPLANON removed due to an
              unfavorable change in their bleeding pattern. 
            </p>
            <Image alt="" src={GRAPHIC_1_IN_10} />
            <p>
              Your healthcare professional can help you manage changes to your
              bleeding pattern.
            </p>
            <p>
              Tell your healthcare professional immediately if you think you may
              be pregnant or your menstrual bleeding is heavy and prolonged.
            </p>
          </div>
        </div>
      </RichText>
    ),
  },
];

export const WTE_SIDE_EFFECTS = [
  {
    questionTitle: 'what are other common side effects of NEXPLANON?',
    questionContent: (
      <div className={styles.sideEffectsList}>
        <ul>
          <li>Headache</li>
          <li>Vaginitis (inflammation of the vagina)</li>
          <li>Weight gain</li>
          <li>Acne</li>
          <li>Breast pain</li>
        </ul>

        <ul>
          <li>Viral infections such as sore throats or flu-like symptoms</li>
          <li>Stomach pain</li>
          <li>Painful periods</li>
          <li>Mood swings, nervousness, or depressed mood</li>
        </ul>

        <ul>
          <li>Back pain</li>
          <li>Nausea</li>
          <li>Dizziness</li>
          <li>Pain</li>
          <li>Pain at the site of insertion</li>
        </ul>
      </div>
    ),
  },
  {
    questionTitle:
      'what other side effects can cause women to stop using NEXPLANON?',
    questionContent: (
      <>
        <div className={`${styles.sideEffects} ${styles.sideEffectsList}`}>
          <ul className={styles.secondList}>
            <li>Mood swings</li>
            <li>Depressed mood</li>
            <li>Weight gain</li>
          </ul>
          <ul className={styles.secondList}>
            <li>Headache</li>
            <li>Acne</li>
          </ul>

          <div className={styles.sideEffectsBox}>
            <p>
              This is not a complete list of possible side effects. For more
              information, ask your healthcare professional for advice about any
              side effects that concern you.{' '}
              <strong>
                You may report side effects to the FDA at{' '}
                <Link
                  href="tel:18003321088"
                  data-design-category="content_link_vd"
                  data-design-type="clickAction"
                  data-design-action="Link"
                  data-design-label="1-800-FDA-1088"
                  data-design-nori_id="Accordion"
                  data-design-module_name="what other side effects could I experience?"
                  data-design-module_no="04"
                  data-design-element_no="01"
                >
                  1-800-FDA-1088
                </Link>
                .
              </strong>
            </p>
          </div>
        </div>
        <RichText className={styles.sideEffectsBottomCopy}>
          <p>
            Implants have been reported to be found in a blood vessel, including
            a blood vessel in the lung. If this happens, you may experience
            shortness of breath, cough, and/or the coughing up of blood or
            blood-stained mucus. Contact your healthcare professional right away
            if you experience any of these symptoms.
          </p>
        </RichText>
      </>
    ),
  },
];

export const FAQ_WTE_INSERTION = [
  {
    title: 'what should I do right after insertion?',
    content: (
      <RichText>
        <p>
          Immediately after the implant is placed, you and your healthcare
          professional should check that it is in your arm by feeling for it.
        </p>
        <p>
          If you cannot feel the implant immediately after insertion, the
          implant may not have been inserted, or it may have been inserted
          deeply. A deep insertion may cause problems with locating and removing
          the implant. When the healthcare professional has located the implant,
          it should be removed.
        </p>
      </RichText>
    ),
  },
  {
    title: 'how should I care for my arm after insertion?',
    content: (
      <RichText>
        <p>
          Following the insertion, a trained healthcare professional will cover
          the site with 2 bandages. The top bandage is a pressure bandage to
          minimize bruising and should be left on for 24 hours. The smaller
          bandage should be kept clean, dry, and in place for 3 to 5 days. 
        </p>
      </RichText>
    ),
  },
  {
    title: 'will I have bruising?',
    content: (
      <RichText>
        <p>
          In a clinical trial, 8.6% of women experienced site reactions. The
          most common complaint was red skin (3.3%), 3% of users reported
          hematoma, and 2% of users reported bruising.
        </p>
      </RichText>
    ),
  },
  {
    title: 'can I get an infection?',
    content: (
      <RichText>
        <p>
          Infection is possible. If you think you may have an infection, contact
          your healthcare professional.
        </p>
      </RichText>
    ),
  },
  {
    title:
      'should I keep using other forms of contraception after having NEXPLANON inserted?',
    content: (
      <RichText>
        <p>
          It depends on when NEXPLANON was inserted relative to your period.
          Talk to your healthcare professional to determine how long you may
          need to use additional contraceptive methods. For example, if
          NEXPLANON is inserted after the fifth day of your period, then you
          should use additional contraceptive methods, such as a condom, for the
          first 7 days after insertion. 
        </p>
        <p>
          NEXPLANON prevents pregnancy for up to 3 years. Implant must be
          removed by the end of the third year. 
        </p>
      </RichText>
    ),
  },
];

export const FAQ_WTE_PERIOD = [
  {
    title:
      'A change to your normal menstrual bleeding pattern is the most common side effect of NEXPLANON',
    content: (
      <RichText>
        <div className={styles.splitBlock}>
          <div>
            <p>
              <strong>1 in 5 women</strong> may stop getting their period
              entirely.
            </p>
            <Image src={GRAPHIC_1_IN_5} />
            <p>
              <strong>1 in 5 women</strong> may experience frequent and/or
              prolonged bleeding.
            </p>
            <Image src={GRAPHIC_1_IN_5} />
            <p>
              Any potential impact to your period may not be immediate and{' '}
              <strong>may take a few months.</strong> Once established, the
              impact to your period within these first 3 months of using
              NEXPLANON is likely predictive of future impact.
            </p>

            <Image className={styles.centeredItem} src={GRAPHIC_MONTH} />
          </div>
          <div>
            <p>
              <strong>1 in 10 women</strong> have NEXPLANON removed due to an
              unfavorable change in their bleeding pattern.
            </p>
            <Image src={GRAPHIC_1_IN_10} />
            <p>
              Your healthcare professional can help you manage any changes to
              your bleeding pattern.
            </p>
            <p>
              Tell your healthcare professional immediately if you think you may
              be pregnant or your menstrual bleeding is heavy and prolonged.
            </p>
          </div>
        </div>
      </RichText>
    ),
  },
];

export const FAQ_WTE_SIDE_EFFECTS = [
  {
    title: 'what are other common side effects of NEXPLANON?',
    content: (
      <RichText>
        <ItemBreakpoint>
          <>
            {SE_LIST_2 && (
              <div className={styles.splittedList}>
                <ul className="tightened">
                  {SE_LIST_2.slice(0, 7).map((item, i) => (
                    <li key={`faq-wte-se-1-${i}`}>{item}</li>
                  ))}
                </ul>
                <ul className="tightened">
                  {SE_LIST_2.slice(7, 15).map((item, i) => (
                    <li key={`faq-wte-se-2-${i}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
          <div className={styles.mobileList}>
            {SE_LIST_2 && (
              <ul className="tightened">
                {SE_LIST_2.map((item, i) => (
                  <li key={`faq-wte-se-single-${i}`}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        </ItemBreakpoint>
        <p>
          Implants have been reported to be found in a blood vessel, including a
          blood vessel in the lung, which can be associated with shortness of
          breath, cough, and/or the coughing up of blood or blood-stained
          mucus. 
        </p>
        <p>
          This is not a complete list of possible side effects. For more
          information, ask your healthcare professional for advice about any
          side effects that concern you.{' '}
          <strong className={styles.italics}>
            You may report side effects to the FDA at
          </strong>{' '}
          <Link
            className={styles.phoneLink}
            href="tel:18003321088"
            data-design-category="content_link_vd"
            data-design-type="clickAction"
            data-design-action="Link"
            data-design-label="1-800-FDA-1088"
            data-design-nori_id="Accordion"
            data-design-module_name="what other side effects could I experience?"
            data-design-module_no="04"
            data-design-element_no="02"
          >
            1-800-FDA-1088
          </Link>
          .
        </p>
      </RichText>
    ),
  },
  {
    title:
      'besides changes in bleeding patterns, what other side effects cause women to stop using NEXPLANON?',
    content: (
      <RichText>
        <ItemBreakpoint>
          <>
            {SE_LIST_1 && (
              <div className={styles.splittedList}>
                <ul className="tightened">
                  {SE_LIST_1.slice(0, 3).map((item, i) => (
                    <li key={`faq-wte-se1-1${i}`}>{item}</li>
                  ))}
                </ul>
                <ul className="tightened">
                  {SE_LIST_1.slice(3, 5).map((item, i) => (
                    <li key={`faq-wte-se1-2-${i}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
          <div className={styles.mobileList}>
            {SE_LIST_1 && (
              <ul className="tightened">
                {SE_LIST_1.map((item, i) => (
                  <li key={`faq-wte-se1-single${i}`}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        </ItemBreakpoint>
      </RichText>
    ),
  },
];

export const FAQ_WTE_PROVIDER = [
  {
    title: 'call your healthcare professional immediately if you have…',
    content: (
      <RichText>
        <Table
          content={SE_TABLE_3}
          title="call your doctor immediately if you have…"
        />
      </RichText>
    ),
  },
  {
    title: 'what if I want to have NEXPLANON removed?',
    content: (
      <RichText>
        <p>
          A trained healthcare professional can remove the implant at any time
          within the 3-year period.* If NEXPLANON is removed, you should use
          another form of contraception, such as condoms, to prevent pregnancy. 
        </p>
        <p className="caption">
          *NEXPLANON must be removed by the end of the third year. 
        </p>
      </RichText>
    ),
  },
];

export const FAQ_WTE_COST = [
  {
    title: 'can you get NEXPLANON for free?*',
    content: (
      <RichText>
        <p>
          You may be able to get <strong>NEXPLANON for free</strong> through
          your insurance.
        </p>
        <p className="caption">
          *Check with your insurance provider about costs and coverage. You may
          have a deductible, copay, or other changes.
        </p>
      </RichText>
    ),
  },
];

export const CALL_HCP_QUESTION = [
  {
    questionTitle: 'Can you get NEXPLANON for free?*',
    questionContent: (
      <RichText className={styles.flexColumn}>
        <p>
          You may be able to get <strong>NEXPLANON for free</strong> through
          your insurance.
        </p>
        <p className="caption">
          *The Affordable Care Act (ACA) covers one type of birth control from
          each of 18 FDA-approved categories of birth control for women without
          charging a co-payment or co-insurance. This coverage includes the arm
          implant NEXPLANON. Some patients may still be responsible for other
          costs associated with the product. This could include fees associated
          with insertion and/or removal procedures.
        </p>
      </RichText>
    ),
  },
];
